"use strict";

import Vivus from "vivus";
import { onReady } from "./ready";

const INLINE_CLASS = "line-enhanced";
const SVG_WRAP_CLASS = "line-svg";
const SVG_PATH_CLASS = "line-svg__path";
const SVG_READY_CLASS = "line-svg--ready";
const HEADING_TAG_SELECTOR = "h1, h2, h3, h4, h5, h6, .claim-description";
const VIVUS_DURATION = 30;
const VIVUS_START_DELAY_MS = 0;
const LOADER_HIDE_FALLBACK_MS = 500;
const EXCLUDED_INLINE_SELECTOR = "header, footer, #pageHeader, #pageFooter, .page-header, .page-footer";
// .claim-description: linee gestite da pages/claim.js (SplitText) poi enhanceInlineLines()
const NO_INJECT_SELECTOR = ".catalog-filters-bar, .filters-collapse, .printable-areas-panel, .filters-list-title, .customization-main, .claim-description";

const UNDERLINE_PATH = "M383.483 0.5C295.78 0.513115 175.88 0.505764 85.2924 0.601731C63.9809 0.624308 42.6695 0.663897 21.3583 0.728894C-4.30541 0.807166 -3.24426 0.6929 8.19164 1.03409C49.5778 2.26883 90.8263 4.40064 132.262 5.20504C177.845 6.08996 223.461 5.49814 269.034 6.40038C282.661 6.67014 296.317 7.10969 309.843 8.18066C313.636 8.48101 316.325 8.81533 309.969 7.92634";
const STRONG_PATH = "M180.722 17.492C146.542 -5.51641 69.7213 -0.488141 18.0164 58.5922C-50.7034 137.114 99.7321 154.906 176.786 92.3527C228.186 50.6262 190.824 13.6931 165.718 0.442296";
const STRIKE_PATH = "M16.0567 3.94359C110.941 3.64739 205.806 2.45336 300.67 1.55399C324.357 1.32943 348.097 -0.316128 371.736 1.01382C378.683 1.40462 357.865 1.73841 350.927 2.07606C269.256 6.05035 187.274 6.63806 105.524 9.81024C75.0476 10.9928 44.6413 12.7384 14.176 14.0027C9.81942 14.1835 -2.41067 13.0284 1.1393 14.8029C3.113 15.7896 12.8492 14.5197 14.5109 14.4776C40.7756 13.8107 67.1126 14.0282 93.4014 14.0283C115.028 14.0283 152.192 13.3856 173.821 13.5575C177.03 13.583 324.426 10.7331 327.872 11.8446C330.961 12.8409 46.0208 21.763 42.7286 22.3092";

function createSvg(type) {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");

    if (type === "underline") {
        svg.setAttribute("viewBox", "0 0 384 9");
        path.setAttribute("d", UNDERLINE_PATH);
    } else if (type === "strong") {
        svg.setAttribute("viewBox", "0 0 203 131");
        path.setAttribute("d", STRONG_PATH);
    } else {
        svg.setAttribute("viewBox", "0 0 374 23");
        path.setAttribute("d", STRIKE_PATH);
    }

    path.setAttribute("class", SVG_PATH_CLASS);
    path.setAttribute("stroke-width", "1");
    svg.appendChild(path);

    return svg;
}

function injectLine(el, type) {
    if (el.closest(EXCLUDED_INLINE_SELECTOR)) return null;
    if (el.classList.contains(INLINE_CLASS)) return null;

    const wrap = document.createElement("span");
    wrap.className = `${SVG_WRAP_CLASS} ${SVG_WRAP_CLASS}--${type}`;
    wrap.setAttribute("aria-hidden", "true");

    const svg = createSvg(type);
    wrap.appendChild(svg);
    el.appendChild(wrap);
    el.classList.add(INLINE_CLASS);

    return svg;
}

function setupAnimations(svgs) {
    if (!svgs.length) return;

    const isInViewport = (el) => {
        if (!el?.getBoundingClientRect) return false;
        const rect = el.getBoundingClientRect();
        const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
        const viewportW = window.innerWidth || document.documentElement.clientWidth || 0;
        return rect.bottom > 0 && rect.right > 0 && rect.top < viewportH && rect.left < viewportW;
    };

    const startSvgAnimation = (svg) => {
        if (svg.dataset.vivusStarted) return;
        svg.dataset.vivusStarted = "1";

        const vivus = new Vivus(svg, {
            type: "oneByOne",
            duration: VIVUS_DURATION,
            start: "manual",
            animTimingFunction: Vivus.EASE
        });

        window.setTimeout(() => {
            svg.closest(`.${SVG_WRAP_CLASS}`)?.classList.add(SVG_READY_CLASS);
            vivus.play();
        }, VIVUS_START_DELAY_MS);
    };

    if (!("IntersectionObserver" in window)) {
        svgs.forEach(svg => {
            startSvgAnimation(svg);
        });
        return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            const svg = entry.target;
            startSvgAnimation(svg);
            obs.unobserve(svg);
        });
    }, {
        threshold: 0.1,
        rootMargin: "0px 0px -10% 0px"
    });

    svgs.forEach(svg => {
        if (isInViewport(svg)) {
            startSvgAnimation(svg);
            return;
        }
        observer.observe(svg);
    });
}

function enhanceInlineLines(root = document, { ignoreNoInject = false } = {}) {
    const scope = root instanceof Element || root === document ? root : document;
    const targets = scope.querySelectorAll("u, s, strong");
    const svgs = [];

    targets.forEach(el => {
        if (!el.closest(HEADING_TAG_SELECTOR)) return;
        if (el.closest(EXCLUDED_INLINE_SELECTOR)) return;
        if (!ignoreNoInject && (el.matches(NO_INJECT_SELECTOR) || el.closest(NO_INJECT_SELECTOR))) return;
        const tag = el.tagName.toLowerCase();
        const type = tag === "s" ? "strike" : (tag === "strong" ? "strong" : "underline");
        const svg = injectLine(el, type);
        if (!svg) return;
        svgs.push(svg);
    });

    setupAnimations(svgs);
    return svgs;
}

function initInlineLines() {
    const cleanupTargets = document.querySelectorAll(NO_INJECT_SELECTOR);
    cleanupTargets.forEach(container => {
        container.querySelectorAll(`.${SVG_WRAP_CLASS}`).forEach(node => node.remove());
        container.querySelectorAll(`.${INLINE_CLASS}`).forEach(node => node.classList.remove(INLINE_CLASS));
        if (container.classList.contains(INLINE_CLASS)) {
            container.classList.remove(INLINE_CLASS);
        }
    });

    enhanceInlineLines(document);
}

function runAfterLoaderHidden(cb) {
    const loader = document.getElementById("siteLoader");
    if (!loader || !window.getComputedStyle) {
        cb();
        return;
    }

    const styles = window.getComputedStyle(loader);
    const opacity = parseFloat(styles.opacity || "0");
    const isStillVisible = loader.classList.contains("is-visible")
        || opacity > 0.01
        || styles.visibility !== "hidden";

    if (!isStillVisible) {
        cb();
        return;
    }

    let done = false;
    const finish = () => {
        if (done) return;
        done = true;
        loader.removeEventListener("transitionend", onTransitionEnd);
        cb();
    };
    const onTransitionEnd = (event) => {
        if (event.target !== loader) return;
        if (event.propertyName && event.propertyName !== "opacity") return;
        finish();
    };

    loader.addEventListener("transitionend", onTransitionEnd);
    window.setTimeout(finish, LOADER_HIDE_FALLBACK_MS);
}

onReady(() => {
    runAfterLoaderHidden(initInlineLines);
});

export { enhanceInlineLines, initInlineLines };
